import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import Loader from "../../Components/Loader";
import "../../Styles/ManageNotes.css";

function ManageNotes() {
    const [formData, setFormData] = useState({
        title: "",
        description: "",
        attachment: null,
    });
    const [loading, setLoading] = useState(false);
    const [pageLoading, setPageLoading] = useState(true);
    const [notes, setNotes] = useState([]);

    useEffect(() => {
        // Fetch notes on component mount
        const fetchNotes = async () => {
            const token = localStorage.getItem("authToken");
            try {
                const response = await axios.get("https://api.uniums.com/api/notes/view-notes", {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                setNotes(response.data.notes);
            } catch (error) {
                console.error("Error fetching notes:", error);
                toast.error("Failed to fetch notes");
            } finally {
                setPageLoading(false);
            }
        };

        fetchNotes();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file && file.size > 5 * 1024 * 1024) {
            // 5 MB limit
            toast.error("File size exceeds 5MB.");
            setFormData({ ...formData, attachment: null });
        } else {
            setFormData({ ...formData, attachment: file });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.title || !formData.attachment) {
            toast.error("Title and file are required.");
            return;
        }

        setLoading(true);
        const token = localStorage.getItem("authToken");
        let formdata = new FormData();

        // Append form data
        formdata.append("title", formData.title);
        formdata.append("description", formData.description);
        formdata.append("attachment", formData.attachment);

        try {
            const response = await axios.post(
                "https://api.uniums.com/api/notes/upload-notes",
                formdata,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            if (response.status === 201) {
                toast.success("Note uploaded successfully");
                setFormData({
                    title: "",
                    description: "",
                    attachment: null,
                });
                setNotes([...notes, response.data.note]);
            }
        } catch (error) {
            console.error("Error uploading note:", error);
            toast.error("Failed to upload note");
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        const token = localStorage.getItem("authToken");

        try {
            await axios.delete(`https://api.uniums.com/api/notes/delete-note/${id}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            setNotes(notes.filter((note) => note._id !== id)); // Remove the deleted note from the state
            toast.success("Note deleted successfully");
        } catch (error) {
            console.error("Error deleting note:", error);
            toast.error("Failed to delete note");
        }
    };

    return (
        <div className="manage-notes-container">
            {pageLoading ? (
                <Loader />
            ) : (
                <>
                    <h1>Manage Notes</h1>
                    <p>Upload and share study materials with ease.</p>

                    <div className="form-container-notes">
                        <form className="upload-form-notes" onSubmit={handleSubmit}>
                            <div className="form-group-notes">
                                <label htmlFor="noteTitle">
                                    Title <span>*</span>
                                </label>
                                <input
                                    type="text"
                                    id="noteTitle"
                                    name="title"
                                    value={formData.title}
                                    onChange={handleChange}
                                    placeholder="Enter the title of the note"
                                    required
                                />
                            </div>

                            <div className="form-group-notes">
                                <label htmlFor="noteDescription">Description</label>
                                <textarea
                                    id="noteDescription"
                                    name="description"
                                    value={formData.description}
                                    onChange={handleChange}
                                    placeholder="Enter a brief description"
                                ></textarea>
                            </div>

                            <div className="form-group-notes">
                                <label htmlFor="fileUpload">
                                    Upload File (Max 5MB) <span>*</span>
                                </label>
                                <input
                                    type="file"
                                    id="fileUpload"
                                    onChange={handleFileChange}
                                />
                            </div>

                            <button type="submit" className="btn-submit-notes" disabled={loading}>
                                {loading ? <Loader small /> : "Upload Note"}
                            </button>
                        </form>
                    </div>

                    <div className="notes-list">
                        <h2>Shared Notes</h2>
                        <ul className="Notes-list-box">
                            {notes.length === 0 ? (
                                <p>No notes available</p>
                            ) : (
                                notes.map((note) => (
                                    <li key={note._id}>
                                        <div className="notes_items">
                                            <span className="note-title">{note.title}</span>
                                            <p className="note-title-para">
                                                <a href={note.attachment} download>
                                                    <AttachFileIcon /> Document
                                                </a>
                                            </p>
                                        </div>
                                        <span className="note-title-time">
                                            {new Date(note.uploadedAt).toLocaleDateString()}&nbsp;&nbsp;
                                            {new Date(note.uploadedAt).toLocaleTimeString()}
                                        </span>
                                        <button
                                            className="btn-delete-notes"
                                            onClick={() => handleDelete(note._id)}
                                        >
                                            Delete
                                        </button>
                                    </li>
                                ))
                            )}
                        </ul>
                    </div>

                    <ToastContainer />
                </>
            )}
        </div>
    );
}

export default ManageNotes;
